import type { ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { SideRail, TopBar, DropdownMenu } from '@vizial-ui/components';

import { useSidebarStore } from '../../store/sidebarStore';
import { useAppSettingsStore } from '../../store/appSettingsStore';
import { ThemeSwitcher } from '../ThemeSwitcher/ThemeSwitcher';
import { TenantSwitcher } from '../TenantSwitcher/TenantSwitcher';
import logoLight from './images/logo-light.svg';
import logoDark from './images/logo-dark.svg';
import styles from './AppShell.module.css';
import { NAV_ITEMS } from './utils/navItems';

export interface AppShellProps {
  children: ReactNode;
}

function handleNavigation(
  key: string,
  navigate: ReturnType<typeof useNavigate>,
) {
  const item = NAV_ITEMS.find((navItem) => navItem.key === key);
  if (item?.path) {
    navigate({ to: item.path });
  }
}

export function AppShell({ children }: AppShellProps) {
  const collapsed = useSidebarStore((state) => state.collapsed);
  const toggle = useSidebarStore((state) => state.toggle);
  const theme = useAppSettingsStore((state) => state.settings.theme);
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const activeItem = NAV_ITEMS.find((item) => item.path === pathname);
  const logo = theme === 'secure-agility-dark' ? logoDark : logoLight;

  return (
    <div className={styles.shell}>
      <div className={styles.railColumn}>
        <SideRail
          items={NAV_ITEMS}
          activeKey={activeItem?.key ?? ''}
          onNavigate={(key) => handleNavigation(key, navigate)}
          collapsed={collapsed}
          onCollapse={toggle}
          logo={
            <Link to="/" className={styles.logo}>
              <img
                src={logo}
                alt="Secure Agility"
                className={styles.logoMark}
              />
              {!collapsed && (
                <span className={styles.logoText}>Secure Agility</span>
              )}
            </Link>
          }
          footer={<TenantSwitcher />}
        />
      </div>

      <div className={styles.mainColumn}>
        <div className={styles.topBarRow}>
          <div className={styles.topBarSlot}>
            <TopBar
              title="Clara"
              user={{ name: 'John Doe', role: 'Administrator' }}
              userMenu={
                <>
                  <DropdownMenu.Item>Profile</DropdownMenu.Item>
                  <DropdownMenu.Item>Settings</DropdownMenu.Item>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item>Sign out</DropdownMenu.Item>
                </>
              }
              showSearch={false}
              showNotifications={false}
              actions={
                <>
                  <ThemeSwitcher />
                </>
              }
            />
          </div>
        </div>
        <main className={styles.content}>{children}</main>
      </div>
    </div>
  );
}
